import DOMPurify from 'dompurify';


function decodeHtmlEntities(str: string): string {
  if (!str) return str;
  return str
    // numeric hex: &#x3c;  /  &#X3C;
    .replace(/&#x([0-9a-fA-F]+);?/g, (_, hex) => {
      try { return String.fromCodePoint(parseInt(hex, 16)); } catch { return ''; }
    })
    // numeric decimal: &#60;
    .replace(/&#(\d+);?/g, (_, dec) => {
      try { return String.fromCodePoint(parseInt(dec, 10)); } catch { return ''; }
    })
    // named entities that matter for XSS (decode &amp; LAST to avoid collapsing layers early)
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, '&');
}


export function cleanText(input: string | null | undefined): string {
  if (!input) return '';


  const normalized = decodeHtmlEntities(input);


  const stripped = DOMPurify.sanitize(normalized, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

  // 3. DOMPurify re-encodes leftover & < > ; decode back to clean plain text.
  return decodeHtmlEntities(stripped).trim();
}



const ALLOWED_TEXT = /^[\p{L}\p{M}\p{N}\p{P}\s+]+$/u;

// VAPT #12/#13 - encoded sequences (%3C, &#60, &#x3c, &lt;) never appear in real plain text,
// so reject them outright; plain %, # and & (C#, 50%, R&D) are still allowed.
const ENCODED_SEQUENCE = /%[0-9a-f]{2}|&#x?[0-9a-f]+|&[a-z][a-z0-9]*;/i;

export function isCleanText(input: string | null | undefined): boolean {
  if (!input) return true;
  if (ENCODED_SEQUENCE.test(input)) return false;
  // Normalize first so "&#60;script&#62;" is checked as "<script>" → rejected.
  return ALLOWED_TEXT.test(decodeHtmlEntities(input));
}


export const CLEAN_TEXT_MESSAGE = 'Please use only letters, numbers and standard punctuation';

/**
 * Adds the isCleanText check to react-hook-form rules, keeping any validate rules already passed.
 * Used by the shared ui/Input and ui/Textarea components.
 */
export function withCleanTextRule<R extends { validate?: any }>(rules: R | undefined, enabled: boolean): R | undefined {
  if (!enabled) return rules;
  const existing = rules?.validate;
  const existing_rules = typeof existing === 'function' ? { custom: existing } : (existing || {});
  return {
    ...(rules || {}),
    validate: {
      ...existing_rules,
      clean_text: (value: unknown) => typeof value !== 'string' || isCleanText(value) || CLEAN_TEXT_MESSAGE,
    },
  } as R;
}

export function cleanRichHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html);
}
